/**
 * -------------------------------------------------------------------------------------------------
 * GLOBO PRODUCT OPTIONS -> CART DRAWER
 * -------------------------------------------------------------------------------------------------
 *
 * Quand la broderie est en jeu, l'app Globo prend la main sur l'ajout au panier : elle intercepte le
 * clic, empêche l'événement `submit` (donc `<product-form>` n'est jamais déclenché), fait son propre
 * `fetch('/cart/add.js')` -- il lui faut ajouter le produit d'add-on en même temps que l'article --
 * puis décide de la suite.
 *
 * Cette décision est une cascade de détections de thèmes connus, dont le dernier cas est une
 * redirection sèche vers /cart. La branche qui nous concerne est celle-ci :
 *
 *   else if (document.querySelector(".header__icon") && document.querySelector("product-form")) {
 *     ...
 *     if (pf.cartDrawer) pf.cartDrawer.renderContents(res, !r && "drawer" === theme?.settings?.afterAtc);
 *   }
 *   else window.location.href = `${GPOConfigs.shopifyRoot}cart`;
 *
 * Stretch a bien un `<product-form>`, mais ni `.header__icon` (une classe Dawn), ni la globale
 * `theme`, ni la propriété `cartDrawer` : on tombait donc dans la redirection, et le panier latéral
 * ne s'ouvrait jamais. Ce fichier fournit les trois pièces manquantes.
 *
 * Les trois sont nécessaires ensemble : n'exposer que `.header__icon` supprimerait la redirection
 * sans rien ouvrir en échange — l'évaluation de `theme?.settings?.afterAtc` lèverait une
 * ReferenceError avant même l'appel à `renderContents`, et le client resterait sans aucun retour.
 *
 * À vérifier à chaque mise à jour de l'app : on s'appuie sur sa détection interne, non documentée.
 */

(() => {
  /* 1. La globale `theme` des thèmes Dawn-like. Globo lit `theme?.settings?.afterAtc` : l'optional
        chaining ne protège pas d'une variable non déclarée, d'où la ReferenceError sans ce stub. */
  window.theme = window.theme || {
    settings: {},
    routes: { cart: `${window.Shopify?.routes?.root ?? '/'}cart` },
  };

  /* 2. Le pont vers le drawer du thème.

        `cart:refresh` réinjecte le contenu de la section (cf. `refreshCart_fn` dans cart-drawer.js),
        et `show()` ouvre le tiroir. On passe volontairement par là, plutôt que par le `cart:change`
        qu'émet `<product-form>` : ce dernier attend un panier portant ses `sections` pré-rendues,
        que l'ajout de Globo ne fournit pas. */
  const cartDrawerBridge = {
    renderContents() {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.getElementById('cart-drawer')?.show();
    },
  };

  /* La propriété est posée sur le prototype, pas sur l'instance : le bloc produit est re-rendu à
     chaque changement de variante, et une propriété d'instance disparaîtrait avec l'ancien nœud. */
  customElements.whenDefined('product-form').then(() => {
    const ProductForm = customElements.get('product-form');

    if (!ProductForm || 'cartDrawer' in ProductForm.prototype) {
      return;
    }

    Object.defineProperty(ProductForm.prototype, 'cartDrawer', {
      configurable: true,
      get: () => cartDrawerBridge,
    });
  });

  /* 3. La classe que Globo cherche pour reconnaître un thème Dawn-like. Elle n'existe pas dans
        Stretch et n'est portée par aucune règle CSS du thème : on la pose sur le lien du panier
        dans l'en-tête, où elle ne sert que de marqueur. */
  const tagCartIcon = () => {
    document
      .querySelector(`a[href$="${window.Shopify?.routes?.root ?? '/'}cart"]`)
      ?.classList.add('header__icon');
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tagCartIcon, { once: true });
  } else {
    tagCartIcon();
  }
})();
